import { createApi } from '@reduxjs/toolkit/query/react';
import { createBackendBaseQuery, transformApiError } from '../../utils/createAuthBaseQuery';
import { API_URLS } from '../../constants/apiUrl';

export interface Fields {
  text: string;
  value: string;
  dependentRole: string;
}

export interface GetMasterDataApiResponse {
  region: Fields[];
  module: Fields[];
  role: Fields[];
  userAccess: Fields[];
}

export interface CreateNewUserResponse {
  id: number;
  title: string;
  status: number;
}

// A single module's region/role/access assignment, as sent to the save API
export interface UserModuleAccess {
  moduleId: number;
  regionIds: number[];
  roleId?: number;
  secondaryRoleId?: number;
  primaryRoleUserAccess?: string[];
  secondaryRoleUserAccess?: string[];
}

// Interface for creating a new user
export interface CreateUserData {
  id?: number;
  email: string;
  firstName: string;
  lastName: string;
  modules: UserModuleAccess[];
}

// Optional interface for partial user data (useful for updates)
export interface CreateUserDataPartial {
  id?: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  modules?: UserModuleAccess[];
}

export interface UserSlimData {
id: number;
email: string;
userName: string;
}

const BASE_URL = process.env.REACT_APP_USER_API_BASE_URL as string;

if (!BASE_URL) {
  throw new Error('REACT_APP_USER_API_BASE_URL environment variable is not set');
}

// Create the base query using the reusable factory
const baseQuery = createBackendBaseQuery(BASE_URL);

export const createNewUserApiSlice = createApi({
  reducerPath: 'createNewUserApi',
  baseQuery: baseQuery,
  endpoints(builders) {
    return {
      fetchMasterData: builders.query<GetMasterDataApiResponse, void>({
        query() {
          return API_URLS.USER_MANAGEMENT.MASTER_DATA;
        },
        transformErrorResponse: transformApiError,
      }),
      createNewUser: builders.mutation<CreateNewUserResponse, { userData: CreateUserData }>({
        query({ userData }) {
          return {
            url: API_URLS.USER_MANAGEMENT.CREATE_USER,
            method: 'POST',
            body: userData,
          };
        },
        transformErrorResponse: transformApiError,
      }),
      // Optional: Add an update user endpoint with partial data
      updateUser: builders.mutation<
        CreateNewUserResponse,
        { userId: number; userData: CreateUserDataPartial }
      >({
        query({ userId, userData }) {
          return {
            url: `${API_URLS.USER_MANAGEMENT.CREATE_USER}/${userId}`,
            method: 'PUT',
            body: userData,
          };
        },
        transformErrorResponse: transformApiError,
      }),
      fetchUserSlimData: builders.query<UserSlimData[], void>({
        query() {
          return API_URLS.USER_MANAGEMENT.SLIM_DATA;
        },
        transformErrorResponse: transformApiError,
      }),
    };
  },
});

export const { useFetchMasterDataQuery, useCreateNewUserMutation, useUpdateUserMutation, useFetchUserSlimDataQuery } =
  createNewUserApiSlice;
